import {
  FormattingToolbar,
  FormattingToolbarContext,
} from "@/components/note/editor/formatting-toolbar";
import { NoteEditorMetadata } from "@/components/note/editor/note-editor-metadata";
import { FatalEditorErrorNotice } from "@/components/note/fatal-editor-error-notice";
import { MentionTooltip } from "@/components/note/mention-tooltip";
import { OutdatedClientNoteNotice } from "@/components/note/outdated-client-note-notice";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { NoteEditorMode, NoteType } from "@/components/note/types";
import { ZIndex } from "@/domains/design/constants";
import { css, cx } from "@/domains/emotion";
import { EmotionClassStyles } from "@/domains/emotion/types";
import { clientEnvModule } from "@/modules/client-env";
import { PublicAppStore, useAppStore, usePublicAppStore } from "@/store";
import { IContactModel } from "@/store/contacts/types";
import { INoteObservable } from "@/store/note/types";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import styled from "@emotion/styled";
import {
  HtmlPlaceholderPreset,
  MemCommonEditor,
  MemCommonEditorAction,
  MemCommonEditorActionKind,
  MemCommonEditorInstance,
  shouldShowFormattingToolbar,
} from "@mem-labs/common-editor";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useRef,
  MutableRefObject,
  MouseEventHandler,
  useState,
} from "react";
import { useDebounceCallback } from "usehooks-ts";
import { NoteEditorBottomBar } from "@/components/note/editor/source-bar/note-editor-bottom-bar";
import { MDSFloatingActionMenuDropdown, useTheme } from "@/mds";
import { ImageLightbox } from "@/components/note/media-viewer/image/image-lightbox";
import { PdfLightbox } from "@/components/note/media-viewer/pdf/pdf-lightbox";
import { useNoteEditorDisplayState } from "@/components/note/note-observable-editor.hooks";
import { NoteObservable } from "@/store/note/NoteObservable";
import { useNoteVersionHistory } from "@/components/note/hooks/use-note-version-history";
import { useGhostTextForCalendarEvent } from "@/components/note/hooks/use-ghost-text-for-calendar-event";
import { EditorSpinner } from "./editor-spinner";
import { EmptyNoteActions } from "@/components/note/editor/empty-note-actions";
import { useMaybeTabContext } from "@/components/layout/tab-context";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";

type NoteObservableEditorProps = EmotionClassStyles & {
  editorStore?: MemCommonEditorStore;
  setEditorStore: (store: MemCommonEditorStore | undefined) => void;
  autoFocus?: boolean;
  autoFocusPosition?: "start" | "end";
  footer?: React.ReactNode;
  goToMention?: INotesViewPageStore["goToMention"];
  onMentionMouseOver?: INotesViewPageStore["onMentionMouseOver"];
  onMentionMouseOut?: INotesViewPageStore["onMentionMouseOut"];
  openFileUploadRejectedModal?: INotesViewPageStore["openFileUploadRejectedModal"];
  openImageUploadRejectedModal?: INotesViewPageStore["openImageUploadRejectedModal"];
  openPdfPageLimitWarningModal: INotesViewPageStore["openPdfPageLimitWarningModal"];
  openRenameFileModal?: INotesViewPageStore["openRenameFileModal"];
  findInNote?: INotesViewPageStore["findInNote"];
  noteObservable: INoteObservable | TemplateObservable;
  myAccount?: IContactModel;
  placeholder?: string;
  showMetadata?: boolean;
  showTopPadding?: boolean;
  mode: NoteEditorMode;
  highlightText?: string;
  onReadOnlyNoteClicked?: () => void;
  noteType?: NoteType;
  onNoteRendered?: (noteId: string) => void;
  htmlPlaceholderPreset?: HtmlPlaceholderPreset;
  containerWidth?: number;
  disableGlobalListeners?: boolean;
  editorModeClassName?: string;
  enableImageLightbox?: boolean;
};

const createEditorInstanceBridgeRef = ({
  editorInstanceRef,
  onEditorAttachedRef,
}: {
  editorInstanceRef: MutableRefObject<MemCommonEditorInstance | null>;
  onEditorAttachedRef: MutableRefObject<() => void>;
}): MutableRefObject<MemCommonEditorInstance | null> => {
  // common-editor writes its instance by mutating `editorInstanceRef.current`.
  // Wrap that write so mem-client can react at attach time, while still reading
  // the real instance from our local ref everywhere else in this component.
  return {
    get current() {
      return editorInstanceRef.current;
    },
    set current(nextEditorInstance: MemCommonEditorInstance | null) {
      editorInstanceRef.current = nextEditorInstance;

      if (!nextEditorInstance) {
        return;
      }

      // Flush startup work exactly when common-editor finishes attaching.
      onEditorAttachedRef.current();
    },
  };
};

export const NoteObservableEditor = observer<NoteObservableEditorProps>(
  function NoteObservableEditor({
    editorStore,
    setEditorStore,
    containerWidth,
    autoFocus = false,
    autoFocusPosition = "start",
    className,
    footer,
    goToMention,
    openFileUploadRejectedModal,
    openImageUploadRejectedModal,
    openPdfPageLimitWarningModal,
    openRenameFileModal,
    findInNote,
    noteObservable,
    myAccount,
    placeholder,
    showMetadata = true,
    showTopPadding = true,
    mode,
    highlightText,
    onReadOnlyNoteClicked,
    noteType = NoteType.Note,
    onNoteRendered,
    htmlPlaceholderPreset,
    disableGlobalListeners = false,
    editorModeClassName,
    enableImageLightbox = false,
  }) {
    const { theme, themeType } = useTheme();
    const { store, pageStore } = useAppStore();
    const maybeTab = useMaybeTabContext();
    const notesViewPage: NotesViewPageStore | undefined = maybeTab
      ? (pageStore.getPageStore(
          maybeTab.activeTab,
          maybeTab.activeTabPath,
          "notesViewPage"
        ) as NotesViewPageStore)
      : undefined;
    const { publicStore } = usePublicAppStore();

    const [editorContainer, setEditorContainer] = useState<HTMLDivElement | null>(null);
    const [lightboxState, setLightboxState] = useState<{
      images: { src: string; alt?: string }[];
      initialIndex: number;
    } | null>(null);
    const [pdfLightboxState, setPdfLightboxState] = useState<{
      src: string;
      fileName: string;
    } | null>(null);
    const appendTo = useCallback(() => {
      if (!editorContainer) return document.body;

      let el: HTMLElement = editorContainer;
      while (el.parentElement && el.clientHeight > 0) {
        el = el.parentElement;
      }
      return el;
    }, [editorContainer]);

    const editorInstanceRef = useRef<MemCommonEditorInstance | null>(null);
    const editorInstanceBridgeRef = useRef<MutableRefObject<MemCommonEditorInstance | null> | null>(
      null
    );
    const queuedEditorActionsRef = useRef<MemCommonEditorAction[]>([]);
    const deferredHighlightActionRef = useRef<MemCommonEditorAction | null>(null);
    const flushQueuedEditorActionsRef = useRef<() => void>(() => undefined);

    const clearQueuedEditorActions = useCallback(() => {
      queuedEditorActionsRef.current = [];
      deferredHighlightActionRef.current = null;
    }, []);

    const flushQueuedEditorActions = useCallback(() => {
      const dispatch = editorInstanceRef.current?.dispatchAction;
      if (!dispatch) {
        return;
      }

      const queuedActions = queuedEditorActionsRef.current;
      queuedEditorActionsRef.current = [];

      queuedActions.forEach(action => {
        dispatch(action);
      });

      if (deferredHighlightActionRef.current) {
        dispatch(deferredHighlightActionRef.current);
      }
    }, []);

    flushQueuedEditorActionsRef.current = flushQueuedEditorActions;

    if (!editorInstanceBridgeRef.current) {
      // Keep a stable ref-shaped bridge so common-editor can assign into it
      // during mount, while mem-client still owns the actual stored instance.
      editorInstanceBridgeRef.current = createEditorInstanceBridgeRef({
        editorInstanceRef,
        onEditorAttachedRef: flushQueuedEditorActionsRef,
      });
    }

    const editorInstanceBridge = editorInstanceBridgeRef.current;

    const handleLightboxClose = useCallback(() => {
      setLightboxState(previousLightboxState => {
        if (!previousLightboxState) {
          return previousLightboxState;
        }

        window.requestAnimationFrame(() => {
          const editor = editorInstanceRef.current?.editor;
          if (!editor?.isEditable) {
            return;
          }

          // focus the editor after closing the lightbox
          editorInstanceRef.current?.dispatchAction?.({
            kind: MemCommonEditorActionKind.FocusEditor,
            payload: null,
          });
        });

        return null;
      });
    }, []);

    const handlePdfLightboxClose = useCallback(() => {
      setPdfLightboxState(previousState => {
        if (!previousState) {
          return previousState;
        }

        window.requestAnimationFrame(() => {
          const editor = editorInstanceRef.current?.editor;
          if (!editor?.isEditable) {
            return;
          }

          editorInstanceRef.current?.dispatchAction?.({
            kind: MemCommonEditorActionKind.FocusEditor,
            payload: null,
          });
        });

        return null;
      });
    }, []);

    const handlePdfPreviewClicked = useCallback((downloadUrl: string, fileName: string) => {
      setPdfLightboxState({ src: downloadUrl, fileName });
    }, []);

    const handleImageClicked = useCallback(
      (imageSrc: string | null, allImages: { src: string; alt?: string }[]) => {
        if (!imageSrc || allImages.length === 0) return;

        // common-editor provides both the clicked src and the gallery src list.
        // Treat those rendered src values as the canonical URLs for the lightbox.
        const initialIndex = allImages.findIndex(img => img.src === imageSrc);
        if (initialIndex < 0) return;

        setLightboxState({ images: allImages, initialIndex });
      },
      []
    );
    const dispatchAction = useCallback(
      (event: MemCommonEditorAction) => {
        if (
          mode !== NoteEditorMode.Editable &&
          event.kind !== MemCommonEditorActionKind.ApplyRemoteUpdate
        )
          return;

        const dispatch = editorInstanceRef.current?.dispatchAction;
        if (dispatch) {
          return dispatch(event);
        }

        // Preserve editor actions that arrive while common-editor is still booting.
        queuedEditorActionsRef.current.push(event);
        return;
      },
      [mode]
    );

    // Sync note version history state with editor diff display
    useNoteVersionHistory(editorStore);

    // Show ghost text for empty notes with upcoming calendar events
    useGhostTextForCalendarEvent(editorStore);

    useHighlighting({
      highlightText,
      editorInstanceRef,
      deferredHighlightActionRef,
      mode,
    });

    const onEditorRendered = useCallback(
      (el: HTMLSpanElement | null) => {
        if (!el || !editorStore?.id) return;
        onNoteRendered?.(editorStore?.id);
      },
      [editorStore?.id, onNoteRendered]
    );

    // Preserve a ref in case state is reset before the component unmounts
    const editorRef = useRef<MemCommonEditorStore>();

    useUnmountEditorStore(
      editorRef,
      noteObservable,
      setEditorStore,
      editorInstanceRef,
      clearQueuedEditorActions
    );
    useNote(
      editorStore,
      noteObservable,
      setEditorStore,
      publicStore,
      editorInstanceRef,
      clearQueuedEditorActions
    );

    // After editorStore is nullified we can reset it
    useEffect(() => {
      if (editorStore) return;

      const newStore = new MemCommonEditorStore({
        autoFocus,
        autoFocusPosition,
        disableGlobalListeners,
        dispatchAction,
        goToMention,
        openFileUploadRejectedModal,
        openImageUploadRejectedModal,
        openPdfPageLimitWarningModal,
        openRenameFileModal,
        findInNote,
        noteObservable,
        myAccount,
        placeholder,
        publicStore,
        appStore: store,
        onReadOnlyNoteClicked,
        onImageClicked: enableImageLightbox ? handleImageClicked : undefined,
        onPdfPreviewClicked: handlePdfPreviewClicked,
        enableImageZoomCursor: enableImageLightbox,
        // Common Editor keeps this off by default; mem-client opts in behind feature flag.
        enableImageHoverToolbar: enableImageLightbox,
        mode,
        theme,
        themeType,
        noteType,
        htmlPlaceholderPreset,
      });

      editorRef.current = newStore;
      setEditorStore(newStore);

      /**
       * The `MemCommonEditorStore` is tightly coupled to our ui - we need to tie
       * some of its lifecycle methods to our component lifecycle.
       *
       * This will ensure the contents of the editor remain up-to-date (E.g. when
       * receiving remote updates).
       *
       * If `mode` is set to "static", the editor will not have any lifecycle
       * methods registered.
       */

      if (mode === NoteEditorMode.Static) {
        return;
      }

      newStore.mount();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorStore]);

    // Update autoFocusPosition when it changes (e.g., from router state)
    useEffect(() => {
      if (editorStore && editorStore.autoFocusPosition !== autoFocusPosition) {
        runInAction(() => {
          editorStore.autoFocusPosition = autoFocusPosition;
        });
      }
    }, [editorStore, autoFocusPosition]);

    useEffect(() => {
      editorStore?.setTheme({ theme, themeType });
    }, [editorStore, theme, themeType]);

    useEffect(() => {
      editorStore?.setOnImageClicked(enableImageLightbox ? handleImageClicked : undefined);
      editorStore?.setImageViewerEnabled(enableImageLightbox);
    }, [editorStore, enableImageLightbox, handleImageClicked]);

    useEffect(() => {
      if (editorStore?.reloadRequired) {
        editorInstanceRef.current = null;
      }
    }, [editorStore?.reloadRequired]);

    const focusEditor: React.MouseEventHandler<HTMLDivElement> = useCallback(
      e => {
        if (mode !== NoteEditorMode.Editable) return;
        e.stopPropagation();

        const editor = editorInstanceRef.current?.editor;
        if (!editor?.isEditable || editor?.isFocused) return;

        // This click can only happen above the editor (otherwise the editor would handle it instead)
        // so we focus the editor and put the cursor at the beginning of the document.
        editor.chain().focus().setTextSelection(0).run();
      },
      [mode]
    );

    const handleScroll = useDebounceCallback(
      () => {
        dispatchAction({
          kind: MemCommonEditorActionKind.ResendMentionUpdate,
          payload: null,
        });
      },
      20,
      { maxWait: 40 }
    );

    const handleMouseMove: MouseEventHandler<HTMLDivElement> = useDebounceCallback(
      event => {
        editorStore?.setMouseY(event.clientY);
      },
      20,
      { maxWait: 40 }
    );

    const showFormattingToolbar = useCallback(
      () => shouldShowFormattingToolbar(editorInstanceRef.current?.editor),
      []
    );

    const displayState = useNoteEditorDisplayState({
      noteObservable,
      editorStore,
      mode,
      notesViewPage,
    });

    if (!editorStore) {
      return <div className={className} translate="no"></div>;
    }

    const { shouldShowBottomBar, shouldShowQuickActions } = displayState;

    const insertMenuContentClassName = css`
      max-width: calc(100vw - 20px);
      display: inline-flex;
      min-width: 176px;
      padding: ${theme.spacing[3]} 0;
      justify-content: center;
      gap: ${theme.spacing["3"]};
      width: ${editorStore?.isInsertMenuActive ? "288px" : "320px"};
      flex-direction: column;
      align-items: flex-start;
      background: ${theme.colors.surface.menu};
      border-radius: ${theme.borderRadius.large};
      border: 1px solid ${theme.colors.border.surface};
      background: ${theme.colors.surface.menu};
      box-shadow: ${theme.shadows.surface};

      & > div {
        width: 100%;
      }
    `;

    return (
      <FormattingToolbarContext.Provider value={editorStore}>
        {mode !== NoteEditorMode.Static && editorStore.editor && !!editorContainer && (
          <FormattingToolbar
            appendTo={appendTo}
            editor={editorStore.editor}
            shouldShow={showFormattingToolbar}
            editorRef={editorInstanceBridge}
          />
        )}
        <EditorContainerStyles
          $mode={mode}
          className={cx(className, editorModeClassName)}
          translate="no"
          onScroll={handleScroll}
          onMouseMoveCapture={handleMouseMove}
          ref={setEditorContainer}
        >
          {showTopPadding && <TopPadding onClick={focusEditor} className={editorModeClassName} />}
          {showMetadata && (
            <NoteEditorMetadata
              className={editorModeClassName}
              store={editorStore}
              onClick={focusEditor}
            />
          )}
          {!editorStore.reloadRequired && (
            <>
              {!editorStore.hasContentError && !editorStore.hasFatalError && (
                <>
                  <MemCommonEditor
                    editorInstanceRef={editorInstanceBridge}
                    editorInitializer={editorStore.editorInitializer}
                    editorEventHandler={editorStore.editorEventHandler}
                    uiTheme={editorStore.uiTheme}
                    footer={footer}
                  />
                  {editorStore.editorContentRendered ? <Invisible ref={onEditorRendered} /> : null}
                </>
              )}
              {editorStore.hasContentError && (
                <OutdatedClientNoteNotice
                  noteId={noteObservable.id}
                  isDesktop={clientEnvModule.isDesktop()}
                />
              )}
              {editorStore.hasFatalError && (
                <FatalEditorErrorNotice
                  noteId={noteObservable.id}
                  isDesktop={clientEnvModule.isDesktop()}
                />
              )}
            </>
          )}
          <MDSFloatingActionMenuDropdown
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.mentionClientRect}
            placement="bottom-start"
            contentListClassName={insertMenuContentClassName}
            contentList={editorStore.mentionContentList}
          />
          <MDSFloatingActionMenuDropdown
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.tableMenuClientRect}
            placement={editorStore.tableMenuPlacement}
            offsetOptions={editorStore.tableMenuOffsetOptions}
            contentList={editorStore.tableMenuContentList}
          />
          <MDSFloatingActionMenuDropdown
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.pasteMenuClientRect}
            placement="bottom-start"
            contentList={editorStore.pasteMenuContentListWithSelection}
          />
          <MDSFloatingActionMenuDropdown
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.fileMenuClientRect}
            placement="bottom-start"
            contentList={editorStore.fileMenuContentList}
            onOpenChange={(open, _event, reason) => {
              if (!open && (reason === "outside-press" || reason === "escape-key")) {
                editorStore.resetFileMenu();
              }
            }}
          />
          <MentionTooltip
            mentionTooltipParams={editorStore.mentionTooltipParams}
            resetMentionTooltipParams={editorStore.resetMentionTooltipParams}
          />
          {/* Overlay spinner for version transitions */}
          <EditorSpinner
            isOverlay
            hideDelay={300}
            show={
              !!notesViewPage?.noteVersionHistory?.isLoading &&
              notesViewPage?.noteVersionHistory?.currentNoteId === editorStore.noteObservable?.id
            }
          />
        </EditorContainerStyles>
        {mode === NoteEditorMode.Editable && (
          <NoteActions
            editorStore={editorStore}
            shouldShowQuickActions={shouldShowQuickActions}
            versionHistoryActive={
              notesViewPage?.noteVersionHistory?.currentNoteId == noteObservable.id
            }
          />
        )}
        {shouldShowBottomBar && noteObservable instanceof NoteObservable && (
          <NoteEditorBottomBar
            note={noteObservable}
            tooltipLabel={editorStore.metadataTooltip}
            containerWidth={containerWidth}
          />
        )}
        {enableImageLightbox && (
          <ImageLightbox
            isOpen={!!lightboxState}
            images={lightboxState?.images}
            initialIndex={lightboxState?.initialIndex}
            context="editor"
            handleClose={handleLightboxClose}
            onOpenChange={open => {
              if (!open) {
                handleLightboxClose();
              }
            }}
          />
        )}
        <PdfLightbox
          isOpen={!!pdfLightboxState}
          src={pdfLightboxState?.src ?? ""}
          fileName={pdfLightboxState?.fileName}
          context="editor"
          handleClose={handlePdfLightboxClose}
          onOpenChange={open => {
            if (!open) handlePdfLightboxClose();
          }}
        />
      </FormattingToolbarContext.Provider>
    );
  }
);

const NoteActions = observer(
  ({
    editorStore,
    shouldShowQuickActions,
  }: {
    editorStore: MemCommonEditorStore;
    versionHistoryActive: boolean;
    shouldShowQuickActions: boolean;
  }) => {
    if (shouldShowQuickActions) {
      return <EmptyNoteActions editorStore={editorStore} />;
    }
    return null;
  }
);

const TopPadding = styled.div`
  padding-top: ${({ theme }) => theme.spacing["5"]};
`;

// Free up store when component unmounts
const useUnmountEditorStore = (
  editorRef: MutableRefObject<MemCommonEditorStore | undefined>,
  noteObservable: INoteObservable | TemplateObservable,
  setEditorStore: (store: MemCommonEditorStore | undefined) => void,
  editorInstanceRef: MutableRefObject<MemCommonEditorInstance | null>,
  clearQueuedEditorActions: () => void
) => {
  useEffectOnMount(() => {
    return () => {
      if (editorRef?.current && noteObservable) {
        editorRef.current.unmount();
        editorInstanceRef.current = null;
        clearQueuedEditorActions();
        setEditorStore(undefined);
      }
    };
  });
};

// Reset editor store to allow TitTap to re-initialize
const useNote = (
  editorStore: MemCommonEditorStore | undefined,
  noteObservable: INoteObservable | TemplateObservable,
  setEditorStore: (store: MemCommonEditorStore | undefined) => void,
  publicStore: PublicAppStore,
  editorInstanceRef: MutableRefObject<MemCommonEditorInstance | null>,
  clearQueuedEditorActions: () => void
) => {
  useEffect(() => {
    if (!editorStore) return;
    if (editorStore.id === noteObservable.id) return;

    if (noteObservable) {
      editorStore.unmount();
    }

    editorInstanceRef.current = null;
    clearQueuedEditorActions();
    setEditorStore(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteObservable.id, publicStore]);
};

// Send highlight actions to the editor
interface UseHighlightingArg {
  highlightText: string | undefined;
  editorInstanceRef: MutableRefObject<MemCommonEditorInstance | null>;
  deferredHighlightActionRef: MutableRefObject<MemCommonEditorAction | null>;
  mode: NoteEditorMode;
}
const useHighlighting = ({
  highlightText,
  editorInstanceRef,
  deferredHighlightActionRef,
  mode,
}: UseHighlightingArg) => {
  useEffect(() => {
    const action: MemCommonEditorAction = {
      kind: MemCommonEditorActionKind.HighlightText,
      payload: {
        text: highlightText ?? "",
        animate: mode === NoteEditorMode.Editable && !!highlightText,
      },
    };

    deferredHighlightActionRef.current = action;

    editorInstanceRef.current?.dispatchAction?.(action);
  }, [deferredHighlightActionRef, editorInstanceRef, highlightText, mode]);
};

const Invisible = styled.span({ display: "none" });

const EditorContainerStyles = styled.div<{ $mode: NoteEditorMode }>`
  position: relative;

  ${({ $mode }) => $mode !== NoteEditorMode.Editable && `scrollbar-width: none;`}

  .tiptap.ProseMirror {
    --max-width: 700px;
  }
`;
